import { useMemo } from 'react';
import { QueryFactory } from 'src/services/api';
import { QualityMetric } from 'src/services/api/api-client';
import { SurveyAnswersProps } from './surveyAnswers';

export const useSurveyAnswersData = ({ answerId, patientId, surveyType }: SurveyAnswersProps) => {
  const singleAnswerQuery = QueryFactory.SurveyQuery.useGetAnswerStatisticQuery(answerId!, patientId, {
    suspense: false,
    enabled: !!answerId,
  });

  const groupAnswerQuery = QueryFactory.SurveyQuery.useGetAnswersStatisticQuery(patientId, surveyType, {
    suspense: false,
    enabled: !answerId,
  });

  const { data: surveysData, isLoading } = useMemo(() => {
    if (answerId) {
      return {
        data: singleAnswerQuery.data,
        isLoading: singleAnswerQuery.isInitialLoading,
      };
    }

    return {
      data: groupAnswerQuery.data,
      isLoading: groupAnswerQuery.isInitialLoading,
    };
  }, [answerId, groupAnswerQuery, singleAnswerQuery]);

  const answersStatisticsList = useMemo(() => {
    return surveysData?.answers?.sort((first, second) =>
      !first || !second || first.answerDate > second.answerDate ? 1 : -1,
    );
  }, [surveysData]);

  const questionsList = surveysData?.questionScheme?.questions ?? null;
  const legend = surveysData?.questionScheme?.legend;

  const answersList = useMemo(() => {
    return answersStatisticsList?.map((answer) => {
      if (!answer) {
        return null;
      }

      const answerScheme = answer.rawAnswer;
      if (!answerScheme) {
        return {
          answerScheme: null,
          answerId: answer.answerId,
          date: answer.answerDate,
          score: null,
          quality: QualityMetric.Empty,
        };
      }

      return {
        answerScheme: answerScheme ?? null,
        date: answer.answerDate,
        score: answer.score,
        quality: answer.quality,
        answeredBy: answer.answeredBy,
        scoreStatus: answer.scoreStatus,
        answerId: answer.answerId,
        canRetryScoreCalculation: answer.canRetryScoreCalculation,
      };
    });
  }, [answersStatisticsList]);

  return { surveysData, isLoading, answersStatisticsList, questionsList, answersList, legend };
};

export type SurveyAnswersData = ReturnType<typeof useSurveyAnswersData>;
